import { Box, Button, Stack, Typography, Paper, Divider } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';

interface ErrorDetails {
    message?: string;
    stack?: string;
    name?: string;
    status?: number;
    statusText?: string;
    data?: any;
}

function getErrorDetails(error: ErrorDetails | string | null | undefined): ErrorDetails {
    if (!error) return {};
    if (typeof error === 'string') return { message: error };
    if (typeof error === 'object' && error !== null && 'message' in error && typeof (error as any).message === 'string') {
        return {
            message: (error as any).message,
            stack: (error as any).stack,
            name: (error as any).name,
        };
    }
    return {
        message: (error as any).message || (error as any).statusText || JSON.stringify(error),
        status: (error as any).status,
        statusText: (error as any).statusText,
        data: (error as any).data,
        stack: (error as any).stack,
    };
}

export const Error = (props) => {
    const navigate = useNavigate();
    const error = useRouteError();
    const details = getErrorDetails(error);

    useEffect(() => {
        document.title = 'Error!';
    }, []);

    return (
        <Box minHeight="80vh" display="flex" justifyContent="center" alignItems="center" bgcolor="#f8f8f8">
            <Paper elevation={3} sx={{ p: 5, minWidth: 350, maxWidth: 700 }}>
                <Stack spacing={2} alignItems="center">
                    <Typography variant="h4" color="error.main" fontWeight="bold">
                        An Error Occurred
                    </Typography>
                    <Divider sx={{ width: '100%' }} />
                    <Typography variant="h6" color="text.secondary">
                        {details.name ? `${details.name}` : 'Error'}
                    </Typography>
                    <Typography variant="body1" color="error.dark" sx={{ wordBreak: 'break-word' }}>
                        {details.message || 'An unexpected error has occurred.'}
                    </Typography>
                    {details.status && (
                        <Typography variant="body2" color="text.secondary">
                            Status: {details.status} {details.statusText && `- ${details.statusText}`}
                        </Typography>
                    )}
                    {details.data && (
                        <Box width="100%">
                            <Typography variant="subtitle2" color="text.secondary">
                                Error Data:
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f5f5f5', fontSize: 13, overflowX: 'auto' }}>
                                <pre style={{ margin: 0 }}>{JSON.stringify(details.data, null, 2)}</pre>
                            </Paper>
                        </Box>
                    )}
                    {details.stack && (
                        <Box width="100%">
                            <Typography variant="subtitle2" color="text.secondary">
                                Stack Trace:
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f5f5f5', fontSize: 12, overflowX: 'auto', maxHeight: 200 }}>
                                <pre style={{ margin: 0 }}>{details.stack}</pre>
                            </Paper>
                        </Box>
                    )}
                    <Button onClick={() => navigate(-1)} variant="contained" color="primary" sx={{ mt: 2 }}>
                        Go Back
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
};
